import { Component, DestroyRef, inject, input, output, viewChild } from '@angular/core';
import { ButtonComponent } from '../button/button.component';
import { ChipComponent } from '../../../components/core/chip/chip.component';
import { GenericTableComponent, TableColumn } from '../../../components/core/generic-table/generic-table.component';
import { MatChipSet } from '@angular/material/chips';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { SearchbarComponent } from '../searchbar/searchbar.component';
import {
  SearchInFhirDirectoryService,
  VzdMappedResult
} from '../../../services/search-in-fhir-directory/search-in-fhir-directory.service';
import { MatDialog } from '@angular/material/dialog';
import { DialogData } from '../dialog/dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DialogService } from '../../../services/dialog/dialog.service';
import { SearchOnHomeserverService } from '../../../services/search-on-homeserver/search-on-homeserver.service';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { RoomService } from '../../../services/room/room.service';
import { Room } from '../../../../shared-api/room';
import { SbMxidOrganisationDialogComponent } from '../../../components/sb-client/sb-mxid-organisation-dialog/sb-mxid-organisation-dialog.component';
import { BaseInviteTestIds } from './base-invite.test-ids';
import { MXID_PATTERN } from 'matrix-js-sdk';

type ResourceType = 'HealthcareService' | 'PractitionerRole' | 'EndpointDirectory';

interface EndpointDialogRow {
  endpoint: string;
}

@Component({
  selector: 'app-base-invite',
  imports: [ButtonComponent, ChipComponent, GenericTableComponent, MatChipSet, MatProgressSpinner, SearchbarComponent],
  templateUrl: './base-invite.html',
  styleUrl: './base-invite.scss',
  host: { '[attr.data-testid]': 'testIds.baseInvite' }
})
/**
 *  A_25428: Suche im VZD-FHIR-Directory und Anzeige der Ergebnisse
 *  A_25479: Bereitstellung eines Search Access Token (im FhirService)
 *  A_26172-01: Nutzung der /search Schnittstelle (im FhirService)
 *  A_26347 - Hinweis vor Einladung weiterer Chatteilnehmer
 */
export class BaseInvite {
  room = input<Room | null>();
  roomName = input<string | null | undefined>();
  isCreateRoomDialog = input<boolean>(false);
  backClick = output<void>();
  resetClick = output<void>();
  confirmClick = output<string | undefined | null>();
  readonly searchbar = viewChild.required<SearchbarComponent>('searchbar');
  /** Ergebnisliste aus dem VZD */
  vzdResults: VzdMappedResult[] = [];
  pageIndex = 0;
  pageSize = 5;
  totalItems = 0;
  allVzdResults: VzdMappedResult[] = [];
  // Such-Optionen für VZD
  selectedDirectory: ResourceType = 'HealthcareService';
  searchValue = '';
  selectedDirectoryChip?: ChipComponent;
  vzdDisplayedColumns: TableColumn<VzdMappedResult>[] = [];
  showSpinner = false;
  isVerzeichnisSelected = false;
  searchPerformed = false;
  protected readonly testIds = BaseInviteTestIds;
  private readonly matDialog = inject(MatDialog);
  private readonly dialogService = inject(DialogService);
  private readonly vzdSearchService = inject(SearchInFhirDirectoryService);
  private readonly searchOnHomeserverService = inject(SearchOnHomeserverService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  readonly inviteForm = this.fb.group({
    mxid: new FormControl('', [Validators.required, Validators.pattern(MXID_PATTERN)])
  });
  private readonly snackbarService = inject(SnackbarService);
  private readonly roomService = inject(RoomService);

  /**
   * A_26347 - Hinweis vor Einladung weiterer Chatteilnehmer
   */
  openInfoDialog(): void {
    if (!this.inviteForm.value.mxid) {
      this.confirmClick.emit(null);
      this.matDialog.closeAll();
    }
    this.vzdResults = [];
    this.allVzdResults = [];
    const dialogData: DialogData = {
      title: 'TEILNEHMER ZU CHAT HINZUFÜGEN',
      svgIcon: 'circle-warning',
      message: 'Wenn Sie diesen Teilnehmer hinzufügen. Bitte achten Sie auf den Datenschutz aller Beteiligten.',
      confirmText: 'Teilnehmer hinzufügen',
      cancelText: 'Abbrechen',
      cancelDataTestId: BaseInviteTestIds.buttonCancelInvite,
      confirmDataTestId: BaseInviteTestIds.buttonConfirmInvite,
      subMessage: 'Möchten sie diese Person trotzdem hinzufügen?',
      dialogActionTestId: 'dialog_invite_actions',
      dialogContentTestId: 'dialog_invite_content'
    };
    this.dialogService
      .openWarningDialog(dialogData, '28.6rem', '28rem')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: confirmed => {
          if (confirmed) {
            this.confirmClick.emit(this.inviteForm.value.mxid);
          }
        },
        complete: () => {
          this.vzdResults = [];
          this.allVzdResults = [];
          this.matDialog.closeAll();
        }
      });
  }

  updatePagedResults(): void {
    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.vzdResults = this.allVzdResults.slice(startIndex, endIndex);
  }

  onVzdPageChange(event: { pageIndex: number; pageSize: number }): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updatePagedResults();
  }

  selectChip(chip: ChipComponent, dir?: ResourceType): void {
    this.vzdResults = [];
    this.allVzdResults = [];
    this.searchPerformed = false;
    this.selectedDirectoryChip = this.selectedDirectoryChip === chip ? undefined : chip;
    this.isVerzeichnisSelected = !dir;
    if (dir) {
      this.selectedDirectory = dir;
      this.searchbar().reset();
    } else {
      this.searchbar().reset();
    }
  }

  resetForm(): void {
    this.vzdResults = [];
    this.allVzdResults = [];
    this.inviteForm.reset({ mxid: '' });
    this.searchPerformed = false;
    this.resetClick.emit();
    this.matDialog.closeAll();
  }

  async onVzdSearch(term: string): Promise<void> {
    this.searchValue = term?.trim() ?? '';
    if (!this.searchValue) {
      this.vzdResults = [];
      this.allVzdResults = [];
      return;
    }
    if (this.isVerzeichnisSelected) {
      await this.performInternalSearch();
    } else {
      await this.performFhirSearch();
    }
  }

  async onVerzeichnisSearch(): Promise<void> {
    const searchTerm = this.inviteForm.value.mxid || '';
    if (searchTerm.trim()) {
      await this.onVzdSearch(searchTerm.trim());
    }
  }

  async performFhirSearch(): Promise<void> {
    this.allVzdResults = [];
    this.totalItems = 0;
    this.vzdDisplayedColumns = [];
    this.searchPerformed = false;

    if (!this.searchValue?.trim()) {
      return;
    }

    this.showSpinner = true;
    this.searchPerformed = true;
    try {
      const fhirSearchResult = await this.vzdSearchService.searchOnFhir({
        directory: this.selectedDirectory,
        searchValue: this.searchValue
      });
      this.allVzdResults = fhirSearchResult.rows;
      this.totalItems = fhirSearchResult.total;
      this.vzdDisplayedColumns = fhirSearchResult.columns;
      this.updatePagedResults();
    } catch (err) {
      this.allVzdResults = [];
      this.totalItems = 0;
      this.vzdDisplayedColumns = [];
      console.error(`There was a Problem when calling performFhirSearch: ${err}`);
    } finally {
      this.showSpinner = false;
    }
  }

  async onSelectVzdRowEvent(event: { element: VzdMappedResult }): Promise<void> {
    const row = event.element;
    if (this.selectedDirectory === 'PractitionerRole' || this.isVerzeichnisSelected) {
      if (!row.matrixId) {
        this.snackbarService.error('Keine Matrix-ID verfügbar.');
        return;
      }
      if (this.isCreateRoomDialog()) {
        this.confirmClick.emit(row.matrixId);
      } else {
        if (!this.room()) return;
        try {
          await this.roomService.inviteIntoRoom(this.room()!.roomId, [{ mxid: row.matrixId }]);
          this.snackbarService.success(`${row.matrixId} wurde eingeladen.`);
          this.matDialog.closeAll();
        } catch (err) {
          console.error(err);
          this.snackbarService.error(`Einladung von ${row.matrixId} fehlgeschlagen.`);
          this.matDialog.closeAll();
        }
      }
      this.matDialog.closeAll();
    } else if (this.selectedDirectory === 'EndpointDirectory') {
      this.confirmClick.emit(row.matrixId);
      this.matDialog.closeAll();
    } else {
      this.openMxidDialog(row);
    }
  }

  onBackBtnClicked(): void {
    this.backClick.emit();
  }

  private async performInternalSearch(): Promise<void> {
    this.showSpinner = true;
    try {
      const result = await this.searchOnHomeserverService.searchOnHomeserver(this.searchValue);
      this.allVzdResults = result.searchResults.map(
        item =>
          ({
            practitionerName: item.displayName || 'Unbekannt',
            matrixId: item.mxId
          }) as VzdMappedResult
      );
      this.totalItems = this.allVzdResults.length;
      this.vzdDisplayedColumns = [
        { key: 'practitionerName', label: 'Name' },
        { key: 'matrixId', label: 'Matrix-ID' }
      ];
      this.updatePagedResults();
    } catch (error) {
      console.error('Internal search failed:', error);
      this.snackbarService.error('Interne Suche fehlgeschlagen');
      this.allVzdResults = [];
      this.vzdResults = [];
    } finally {
      this.showSpinner = false;
    }
  }

  private openMxidDialog(row: VzdMappedResult): void {
    if (!row.endpoints || row.endpoints.length === 0) {
      this.snackbarService.error('Keine Matrix-IDs verfügbar.');
      return;
    }

    const dialogRows: EndpointDialogRow[] = row.endpoints.map(ep => ({ endpoint: ep }));

    const roomData = {
      room: this.room()
    };

    this.matDialog
      .open(SbMxidOrganisationDialogComponent, {
        width: '50rem',
        data: {
          rows: dialogRows,
          serviceName: row.hsName,
          roomInfo: roomData
        }
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }
}
